import { SplitContentConfig, SplitContentNode, SplitContentNodeContent } from "./splitContentTypes";

const SNAP_TO_GRID_SIZE = 5;

export const onDragging = (prevLayout: SplitContentNode, nodeId: string, newSizes: number[]) => {
  const updateNode = (node: SplitContentNode): SplitContentNode => {
    if (node.id === nodeId && node.children) {
      // Round sizes to nearest 5
      const roundedSizes = newSizes.map((size) => Math.round(size / SNAP_TO_GRID_SIZE) * SNAP_TO_GRID_SIZE);

      //   // Ensure sizes add up to 100?
      //   const totalSize = roundedSizes.reduce((acc, size) => acc + size, 0);
      //   if (totalSize !== 100) {
      //     const diff = 100 - totalSize;
      //     roundedSizes[roundedSizes.length - 1] += diff;
      //   }

      return {
        ...node,
        children: node.children.map((child, index) => ({
          ...child,
          size: roundedSizes[index],
        })),
      };
    }

    if (node.children) {
      return {
        ...node,
        children: node.children.map(updateNode),
      };
    }

    return node;
  };

  return updateNode(prevLayout);
};

export const handleSplit = (
  prevLayout: SplitContentNode,
  selectedNode: SplitContentNode,
  direction: "horizontal" | "vertical"
) => {
  const updateNode = (node: SplitContentNode): SplitContentNode => {
    if (node.id === selectedNode.id) {
      return {
        id: node.id,
        type: "split",
        direction,
        children: [
          { id: crypto.randomUUID(), type: "content", size: 50 },
          { id: crypto.randomUUID(), type: "content", size: 50 },
        ],
        size: 50,
      };
    }

    if (node.children) {
      return {
        ...node,
        children: node.children.map((child) => updateNode(child)),
      };
    }

    return node;
  };

  return updateNode(prevLayout);
};

export const handleDelete = (prevLayout: SplitContentNode, nodeId: string) => {
  const deleteNode = (node: SplitContentNode): SplitContentNode | null => {
    // If this is a split node with children
    if (node.children) {
      // Find the index of the child to delete
      const childIndex = node.children.findIndex((child) => child.id === nodeId);

      if (childIndex !== -1) {
        // If we found the node to delete
        const remainingChild = node.children[childIndex === 0 ? 1 : 0];
        // Return the sibling node, effectively removing the split
        return remainingChild;
      }

      // If we didn't find the node in direct children, recurse deeper
      const newChildren = node.children
        .map((child) => deleteNode(child))
        .filter((child): child is SplitContentNode => child !== null);

      if (newChildren.length === 1) {
        // If only one child remains after deletion, return it directly
        return newChildren[0];
      }

      return {
        ...node,
        children: newChildren,
      };
    }

    // If this is the node to delete, return null
    if (node.id === nodeId) {
      return null;
    }

    // Otherwise return the node unchanged
    return node;
  };

  const newLayout = deleteNode(prevLayout);
  const rootNode: SplitContentNodeContent = {
    id: "root",
    type: "content",
  };

  return newLayout || rootNode;
};

export const updateNodeById = (
  root: SplitContentNode,
  nodeId: string,
  updater: (node: SplitContentNode) => SplitContentNode
): SplitContentNode => {
  if (root.id === nodeId) {
    return updater(root);
  }

  if (root.children) {
    return {
      ...root,
      children: root.children.map((child) => updateNodeById(child, nodeId, updater)),
    };
  }

  return root;
};

export const findNodeAndUpdateConfig = (
  prevLayout: SplitContentNode,
  nodeId: string,
  newConfig: SplitContentConfig
) => {
  const updatedLayout = updateNodeById(prevLayout, nodeId, (node) => {
    const newNode: SplitContentNodeContent = {
      ...node,
      type: "content",
      config: newConfig,
    };
    return newNode;
  });
  return updatedLayout;
};
